import { InputNumber } from "@/components/InputNumber";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateCommissionsSettingsRanks,
  useEnumeration,
  useUpdateCommissionsSettingsRanks,
} from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { CommissionsSettingsRankSelect } from "../../../../enumerations/components/form/CommissionsSettingsRankSelect";
import { EnumerationType } from "../../../../enumerations/enums";
import { CommissionsSettingsRank, CreateUpdateCommissionsSettingsRank } from "../../types";
import { convertCommissionsSettingsRankToCreateUpdateObject } from "../../utils";

type Props = {
  rank?: CommissionsSettingsRank;
  isOpen: boolean;
  onFormCancel: () => void;
};

export const CommissionsSettingsRankModalForm = ({ isOpen, rank, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const ranksEnums = useEnumeration(EnumerationType.COMMISSIONS_SETTINGS_RANKS);

  const [form] = Form.useForm<CreateUpdateCommissionsSettingsRank>();

  const createCommissionsSettingsRanks = useCreateCommissionsSettingsRanks();
  const updateCommissionsSettingsRanks = useUpdateCommissionsSettingsRanks();

  useEffect(() => {
    if (isOpen && rank) {
      const formData = convertCommissionsSettingsRankToCreateUpdateObject(rank);

      form.setFieldsValue(formData);
    }
  }, [isOpen, rank, form]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      if (rank) {
        updateCommissionsSettingsRanks.mutate(
          { id: rank.id, object: values },
          {
            onSuccess: onFormCancel,
            onError: (error) => {
              setErrorsToForm(form, "commissions.rank.attrs", error.violations);
            },
          },
        );
      } else {
        createCommissionsSettingsRanks.mutate(values, {
          onSuccess: onFormCancel,
          onError: (error) => {
            setErrorsToForm(form, "commissions.rank.attrs", error.violations);
          },
        });
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={isOpen}
      title={rank ? t("commissions.rank.titles.updateRank") : t("commissions.rank.titles.createRank")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createCommissionsSettingsRanks.isPending || updateCommissionsSettingsRanks.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={rank} />

      <Form form={form} layout="vertical" name="commissionsSettingsRankForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={5}>
            <Form.Item
              name="orderIndex"
              label={t("commissions.rank.attrs.orderIndex")}
              rules={[validations.notNull, validations.minNumber(1)]}
            >
              <InputNumber />
            </Form.Item>
          </Col>

          <Col span={19}>
            <Form.Item
              name="name"
              label={t("commissions.rank.attrs.name")}
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item name="nextRankId" label={t("commissions.rank.attrs.nextRankId")} rules={[validations.none]}>
              <CommissionsSettingsRankSelect
                allowClear
                dataSource={rank ? ranksEnums.filter((rankEnum) => rankEnum.id !== rank.id) : undefined}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
